import React, { useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DateInputIt } from "@/components/DateInputIt";
import { Upload, AlertTriangle, Loader2, Sparkles } from "lucide-react";

const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

// Caricamento fattura provvigione (agenzia / master) con controllo del totale rispetto alla pre-fattura.
export function InvoiceUploadDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ numero: "", data: new Date().toISOString().slice(0, 10), totale: row?.totale_fattura ?? "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [reading, setReading] = useState(false);
  if (!row) return null;
  const tot = parseFloat(String(f.totale).replace(",", "."));
  const mismatch = !Number.isNaN(tot) && Math.abs(tot - row.totale_fattura) > 0.01;

  const pick = async (e) => {
    const x = e.target.files?.[0];
    if (!x) return;
    if (x.size > 5 * 1024 * 1024) { toast.error("Il file supera 5 MB"); return; }
    const up = { nome: x.name, contentType: x.type || "application/pdf", base64: await readFile(x) };
    setFile(up);
    setReading(true);
    try {
      const { data } = await api.post("/greenpower/commissions/extract-invoice", up);
      const d = data.data || {};
      setAi(d);
      setF((s) => ({ numero: d.numero || s.numero, data: /^\d{4}-\d{2}-\d{2}$/.test(d.data || "") ? d.data : s.data, totale: d.totale ?? s.totale }));
      toast.success("Dati letti dalla fattura: controlla e invia");
    } catch (e) { toast.warning(formatApiErrorDetail(e.response?.data?.detail) || "Lettura automatica non riuscita: compila a mano"); }
    finally { setReading(false); }
  };
  const send = async () => {
    if (!f.numero.trim() || !f.data || Number.isNaN(tot) || !file) { toast.error("Compila numero, data, totale e allega la fattura"); return; }
    setBusy(true);
    try { await api.post(`/greenpower/commissions/${row.id}/fattura`, { numero: f.numero, data: f.data, totale: tot, file, ai }); toast.success("Fattura inviata: l'admin è stato avvisato"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="gp-inv-dialog">
        <DialogHeader><DialogTitle>Carica fattura — {row.id_esterno}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Totale atteso dalla pre-fattura: <strong data-testid="gp-inv-expected">{euro(row.totale_fattura)}</strong> (netto a pagare {euro(row.net)})</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Numero fattura *</Label><Input className="mt-1.5" data-testid="gp-inv-numero" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} /></div>
          <div><Label>Data *</Label><DateInputIt className="mt-1.5" fromYear={2015} value={f.data} onChange={(v) => setF((s) => ({ ...s, data: v }))} testid="gp-inv-data" /></div>
        </div>
        <div><Label>Totale fattura (€) *</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="gp-inv-totale" value={f.totale} onChange={(e) => setF({ ...f, totale: e.target.value })} /></div>
        {mismatch && <p className="text-xs text-amber-500 flex items-center gap-1.5" data-testid="gp-inv-mismatch"><AlertTriangle className="w-3.5 h-3.5" /> Il totale è diverso dalla pre-fattura: puoi inviarla, ma l'admin la controllerà.</p>}
        <div><Label>File (PDF o foto, max 5 MB) * — i campi si compilano da soli</Label><Input type="file" accept=".pdf,image/*" className="mt-1.5" data-testid="gp-inv-file" onChange={pick} disabled={reading} /></div>
        {reading && <p className="text-sm text-primary flex items-center gap-2" data-testid="gp-inv-reading"><Loader2 className="w-4 h-4 animate-spin" /> Lettura fattura con AI…</p>}
        {ai && !reading && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="gp-inv-ai"><Sparkles className="w-3.5 h-3.5 text-primary" />
            Letto: {ai.emittente || "—"}{ai.partita_iva_emittente ? ` · P.IVA ${ai.partita_iva_emittente}` : ""}{ai.imponibile != null ? ` · imponibile ${euro(ai.imponibile)}` : ""}{ai.iva != null ? ` · IVA ${euro(ai.iva)}` : ""}{ai.ritenuta != null ? ` · ritenuta ${euro(ai.ritenuta)}` : ""}
          </p>
        )}
        <Button onClick={send} disabled={busy || reading} className="gap-2" data-testid="gp-inv-submit"><Upload className="w-4 h-4" /> {busy ? "Invio…" : "Invia fattura"}</Button>
      </DialogContent>
    </Dialog>
  );
}
